import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { dbService } from '../services/dbService';
import { settingsService } from '../services/settingsService';

const AppContext = createContext();

const initialState = {
  isLoading: true,
  settings: {
    currency: 'AUD',
    financialYearStart: '07-01',
    importantCategories: [],
    dateFormat: 'system',
    reducedMotion: false,
    autoBackup: false,
    lastBackup: null,
    backupFolder: null,
  },
  transactions: [],
  categories: [],
  budgets: [],
  people: [],
  recurringRules: [],
  ious: [],
  filters: {
    dateRange: null,
    categories: [],
    accounts: [],
    people: [],
    searchText: '',
  },
  ui: {
    showSplash: true,
    sidebarOpen: false,
    currentView: 'current',
    accountingMode: 'accrual',
    selectedTransactions: [],
    editingTransaction: null,
    showCategoryManager: false,
  },
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'HYDRATE_SYNCED_WORKSPACE':
      return { ...state, ...action.payload, settings: { ...state.settings, ...action.payload.settings } };
    case 'SET_TRANSACTIONS':
      return { ...state, transactions: action.payload };
    case 'ADD_TRANSACTION':
      return { ...state, transactions: [...state.transactions, action.payload] };
    case 'ADD_MULTIPLE_TRANSACTIONS':
      return { ...state, transactions: [...state.transactions, ...action.payload] };
    case 'UPDATE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.map(t =>
          t.id === action.payload.id ? action.payload : t
        ),
      };
    case 'DELETE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.filter(t => t.id !== action.payload),
      };
    case 'DELETE_MULTIPLE_TRANSACTIONS':
      return {
        ...state,
        transactions: state.transactions.filter(t => !action.payload.includes(t.id)),
      };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'ADD_CATEGORY':
      return { ...state, categories: [...state.categories, action.payload] };
    case 'UPDATE_CATEGORY':
      return {
        ...state,
        categories: state.categories.map(c =>
          c.id === action.payload.id ? action.payload : c
        ),
      };
    case 'DELETE_CATEGORY':
      return {
        ...state,
        categories: state.categories.filter(c => c.id !== action.payload),
      };
    case 'SET_BUDGETS':
      return { ...state, budgets: action.payload };
    case 'ADD_BUDGET':
      return { ...state, budgets: [...state.budgets, action.payload] };
    case 'UPDATE_BUDGET':
      return {
        ...state,
        budgets: state.budgets.map(b =>
          b.id === action.payload.id ? action.payload : b
        ),
      };
    case 'DELETE_BUDGET':
      return {
        ...state,
        budgets: state.budgets.filter(b => b.id !== action.payload),
      };
    case 'SET_PEOPLE':
      return { ...state, people: action.payload };
    case 'ADD_PERSON':
      return { ...state, people: [...state.people, action.payload] };
    case 'UPDATE_PERSON':
      return {
        ...state,
        people: state.people.map(p =>
          p.id === action.payload.id ? action.payload : p
        ),
      };
    case 'DELETE_PERSON':
      return {
        ...state,
        people: state.people.filter(p => p.id !== action.payload),
      };
    case 'SET_RECURRING_RULES':
      return { ...state, recurringRules: action.payload };
    case 'ADD_RECURRING_RULE':
      return { ...state, recurringRules: [...state.recurringRules, action.payload] };
    case 'UPDATE_RECURRING_RULE':
      return {
        ...state,
        recurringRules: state.recurringRules.map(r =>
          r.id === action.payload.id ? action.payload : r
        ),
      };
    case 'DELETE_RECURRING_RULE':
      return {
        ...state,
        recurringRules: state.recurringRules.filter(r => r.id !== action.payload),
      };
    case 'SET_IOUS':
      return { ...state, ious: action.payload };
    case 'ADD_IOU':
      return { ...state, ious: [...state.ious, action.payload] };
    case 'UPDATE_IOU':
      return {
        ...state,
        ious: state.ious.map(i =>
          i.id === action.payload.id ? action.payload : i
        ),
      };
    case 'DELETE_IOU':
      return {
        ...state,
        ious: state.ious.filter(i => i.id !== action.payload),
      };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'SET_UI':
      return { ...state, ui: { ...state.ui, ...action.payload } };
    case 'SET_EDITING_TRANSACTION':
      return { ...state, ui: { ...state.ui, editingTransaction: action.payload } };
    case 'TOGGLE_CATEGORY_MANAGER':
      return { ...state, ui: { ...state.ui, showCategoryManager: !state.ui.showCategoryManager } };
    case 'HIDE_SPLASH':
      return { ...state, ui: { ...state.ui, showSplash: false } };
    case 'TOGGLE_SIDEBAR':
      return { ...state, ui: { ...state.ui, sidebarOpen: !state.ui.sidebarOpen } };
    case 'TOGGLE_VIEW':
      return {
        ...state,
        ui: {
          ...state.ui,
          currentView: state.ui.currentView === 'current' ? 'future' : 'current',
        },
      };
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      dispatch({
        type: 'SET_SETTINGS',
        payload: { reducedMotion: prefersReducedMotion },
      });

      // Initialize database
      await dbService.init();
      
      // Load settings
      const settings = await settingsService.getSettings();
      dispatch({ type: 'SET_SETTINGS', payload: settings });

      // Load all data
      await loadData();

      // Hide splash after data is loaded
      setTimeout(() => {
        dispatch({ type: 'HIDE_SPLASH' });
        dispatch({ type: 'SET_LOADING', payload: false });
      }, 1500);
    } catch (error) {
      console.error('Failed to initialize app:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'HIDE_SPLASH' });
    }
  };

  const loadData = async () => {
    try {
      const [transactions, categories, budgets, people, recurringRules, ious] = await Promise.all([
        dbService.getTransactions(),
        dbService.getCategories(),
        dbService.getBudgets(),
        dbService.getPeople(),
        dbService.getRecurringRules(),
        dbService.getIOUs(),
      ]);

      dispatch({ type: 'SET_TRANSACTIONS', payload: transactions });
      dispatch({ type: 'SET_CATEGORIES', payload: categories });
      dispatch({ type: 'SET_BUDGETS', payload: budgets });
      dispatch({ type: 'SET_PEOPLE', payload: people });
      dispatch({ type: 'SET_RECURRING_RULES', payload: recurringRules });
      dispatch({ type: 'SET_IOUS', payload: ious });
    } catch (error) {
      console.error('Failed to load data:', error);
      throw error;
    }
  };

  // Refresh the entire in-memory financial workspace after a verified,
  // atomic local import. Never leave Dashboard with the old transactions while
  // other screens read the newly restored IndexedDB records.
  const refreshFromDatabase = async () => {
    const [transactions,categories,budgets,people,recurringRules,ious,settings] = await Promise.all([
      dbService.getTransactions(),dbService.getCategories(),dbService.getBudgets(),
      dbService.getPeople(),dbService.getRecurringRules(),dbService.getIOUs(),
      settingsService.getSettings()
    ]);
    dispatch({type:'HYDRATE_SYNCED_WORKSPACE',payload:{
      transactions,categories,budgets,people,recurringRules,ious,settings
    }});
  };
  useEffect(()=>{
    const onImport=event=>{
      if(event.detail?.userId===dbService.getWorkspaceUser())
        void refreshFromDatabase().catch(error=>console.error('Could not refresh the financial workspace',error));
    };
    const onStorage=event=>{
      const owner=dbService.getWorkspaceUser();
      if(owner&&event.key?.startsWith('bpa-expense-sync-base:')&&event.key.includes(':'+owner+':'))
        void refreshFromDatabase().catch(error=>console.error('Could not update local workspace after another-tab sync',error));
    };
    window.addEventListener('bpa:cloud-imported',onImport);
    window.addEventListener('storage',onStorage);
    return ()=>{
      window.removeEventListener('bpa:cloud-imported',onImport);
      window.removeEventListener('storage',onStorage);
    };
  },[]);

  const value = {
    state,
    dispatch,
    actions: {
      loadData,
      refreshFromDatabase,
      hideSplash: () => dispatch({ type: 'HIDE_SPLASH' }),
      toggleSidebar: () => dispatch({ type: 'TOGGLE_SIDEBAR' }),
      toggleView: () => dispatch({ type: 'TOGGLE_VIEW' }),
      setAccountingMode: (mode) => {
        if (mode !== 'cash' && mode !== 'accrual') throw new Error('Invalid accounting basis');
        dispatch({ type: 'SET_UI', payload: { accountingMode: mode } });
      },
      setFilters: (filters) => dispatch({ type: 'SET_FILTERS', payload: filters }),
      addTransaction: (transaction) => dispatch({ type: 'ADD_TRANSACTION', payload: transaction }),
      addMultipleTransactions: (transactions) => dispatch({ type: 'ADD_MULTIPLE_TRANSACTIONS', payload: transactions }),
      updateTransaction: (transaction) => dispatch({ type: 'UPDATE_TRANSACTION', payload: transaction }),
      deleteTransaction: (id) => dispatch({ type: 'DELETE_TRANSACTION', payload: id }),
      deleteMultipleTransactions: (ids) => dispatch({ type: 'DELETE_MULTIPLE_TRANSACTIONS', payload: ids }),
      setEditingTransaction: (transaction) => dispatch({ type: 'SET_EDITING_TRANSACTION', payload: transaction }),
      addCategory: (category) => dispatch({ type: 'ADD_CATEGORY', payload: category }),
      updateCategory: (category) => dispatch({ type: 'UPDATE_CATEGORY', payload: category }),
      deleteCategory: (id) => dispatch({ type: 'DELETE_CATEGORY', payload: id }),
      toggleCategoryManager: () => dispatch({ type: 'TOGGLE_CATEGORY_MANAGER' }),
      addBudget: async (budget) => {
        const { id, ...data } = budget;
        const savedId = await dbService.addBudget(data);
        dispatch({ type: 'ADD_BUDGET', payload: { ...data, id: savedId } });
        return savedId;
      },
      updateBudget: async (budget) => {
        const saved = await dbService.updateBudget(budget.id, budget);
        if (!saved) throw new Error('Budget not found');
        dispatch({ type: 'UPDATE_BUDGET', payload: saved });
        return saved;
      },
      deleteBudget: async (id) => {
        await dbService.deleteBudget(id);
        dispatch({ type: 'DELETE_BUDGET', payload: id });
      },
      addPerson: async (person) => {
        const { id, ...data } = person;
        const savedId = await dbService.addPerson(data);
        dispatch({ type: 'ADD_PERSON', payload: { ...data, id: savedId } });
        return savedId;
      },
      updatePerson: async (person) => {
        const saved = await dbService.updatePerson(person.id, person);
        if (!saved) throw new Error('Person not found');
        dispatch({ type: 'UPDATE_PERSON', payload: saved });
        return saved;
      },
      deletePerson: async (id) => {
        const relevant = await dbService.getIOUs();
        if (relevant.some(entry => String(entry.personId) === String(id))) {
          throw new Error('Cannot delete a person with IOU history');
        }
        await dbService.deletePerson(id);
        dispatch({ type: 'DELETE_PERSON', payload: id });
      },
      addRecurringRule: (rule) => dispatch({ type: 'ADD_RECURRING_RULE', payload: rule }),
      updateRecurringRule: (rule) => dispatch({ type: 'UPDATE_RECURRING_RULE', payload: rule }),
      deleteRecurringRule: (id) => dispatch({ type: 'DELETE_RECURRING_RULE', payload: id }),
      addIOU: async (iou) => {
        if (iou.createTransaction) {
          const person = state.people.find(p => String(p.id) === String(iou.personId));
          if (!person) throw new Error('Person not found for linked transfer');
          const saved = await dbService.addIOUAndTransaction(iou, person.name);
          dispatch({ type: 'ADD_IOU', payload: saved.iou });
          dispatch({ type: 'ADD_TRANSACTION', payload: saved.transaction });
          return saved.iou.id;
        }
        const { id, createTransaction, ...data } = iou;
        const savedId = await dbService.addIOU(data);
        dispatch({ type: 'ADD_IOU', payload: { ...data, id: savedId } });
        return savedId;
      },
      updateIOU: async (iou) => {
        const saved = await dbService.updateIOUAndLinkedTransaction(iou.id, iou);
        dispatch({ type: 'UPDATE_IOU', payload: saved.iou });
        if (saved.transaction) dispatch({ type: 'UPDATE_TRANSACTION', payload: saved.transaction });
        return saved.iou;
      },
      deleteIOU: async (id) => {
        const linkedTransactionId = await dbService.deleteIOUAndLinkedTransaction(id);
        dispatch({ type: 'DELETE_IOU', payload: id });
        if (linkedTransactionId != null) {
          dispatch({ type: 'DELETE_TRANSACTION', payload: linkedTransactionId });
        }
      },
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};